// src/components/public_utilities/pu/PuOverviewTab.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities v2 · Overview tab
// --- UPDATE (Public Utilities v2) — NEW FILE ------------------------------------------------
// KPI tiles from the intelligence overview, "Needs attention" recommendations (each one runs
// its action: open a tab, filter the registry, bulk-schedule, place in zones), assets by type,
// risk distribution, and the FlowSense AI card (plain-language state + "how this system works"
// + Ask FlowSense). Children (utility status grid, lifecycle cost) render underneath.
// --- END UPDATE ------------------------------------------------------------------------------
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, SimpleGrid, VStack, HStack, Text, Badge, Button, Icon, Skeleton, Collapse, Input, InputGroup, InputRightElement,
    IconButton, Alert, AlertIcon, Spinner, Flex, Tooltip,
} from '@chakra-ui/react';
import {
    MdInventory2, MdFactCheck, MdEventBusy, MdEventNote, MdReportProblem, MdGroups, MdLocationOff, MdDataUsage,
    MdPsychology, MdLightbulb, MdSend, MdExpandMore, MdExpandLess, MdArrowForward, MdRefresh, MdCategory, MdShield,
} from 'react-icons/md';
import { PuCard, KpiTile, BarList, EmptyState } from './PuPrimitives';
import { usePuColors, fmtPct, PRIORITY_SCHEME, RISK_SCHEME } from './puUtils';
import { getBrief, askPublicUtilitiesAi } from '../../../services/publicUtilitiesApi';

function PuOverviewTab({ intel, onAction, children }) {
    const c = usePuColors();
    const o = intel.overview;
    const t = o?.totals;
    const [brief, setBrief] = useState(null);
    const [briefErr, setBriefErr] = useState(null);
    const [showGuide, setShowGuide] = useState(false);
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState(null);
    const [asking, setAsking] = useState(false);

    const loadBrief = useCallback(() => {
        setBriefErr(null);
        getBrief().then(setBrief).catch((e) => setBriefErr(e.message));
    }, []);
    useEffect(() => { loadBrief(); }, [loadBrief, o?.generated_at]);

    const ask = useCallback(async (q) => {
        const text = String(q ?? question).trim();
        if (!text) return;
        setAsking(true); setAnswer(null);
        try {
            const r = await askPublicUtilitiesAi(text, brief?.context);
            setAnswer({ text: r?.answer || r?.text || 'No answer returned.', source: r?.source || 'ollama', model: r?.model || null });
        } catch (e) {
            // FlowSense AI offline → answer from the built-in guide + live summary (no invented numbers)
            setAnswer({ text: [...(brief?.summary || []), '', ...(brief?.guide || []).slice(0, 3)].join('\n'), source: 'built-in', error: e.message });
        } finally { setAsking(false); }
    }, [question, brief]);

    const go = (n, action) => () => onAction?.(action, n);

    return (
        <VStack spacing={5} align="stretch">
            {intel.error && <Alert status="warning" borderRadius="xl" fontSize="sm"><AlertIcon />{intel.error}</Alert>}

            <SimpleGrid columns={{ base: 2, md: 4, xl: 8 }} spacing={3}>
                {intel.loading && !t ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} h="72px" borderRadius="xl" />) : (
                    <>
                        <KpiTile label="Assets" value={t?.assets} icon={MdInventory2} scheme="teal" onClick={go(null, { type: 'tab', tab: 'registry' })} />
                        <KpiTile label="Inspected (12 mo)" value={fmtPct(t?.inspection_compliance_pct)} icon={MdFactCheck} scheme="green" hint={`${t?.inspected_12m ?? '—'} assets inspected in the last 12 months`} />
                        <KpiTile label="Overdue inspections" value={t?.overdue} icon={MdEventBusy} scheme={t?.overdue ? 'red' : 'gray'} onClick={go(null, { type: 'tab', tab: 'inspections', filter: { state: 'overdue' } })} />
                        <KpiTile label="No schedule" value={t?.unscheduled} icon={MdEventNote} scheme="orange" onClick={go(null, { type: 'bulk_schedule', only_unscheduled: true })} />
                        <KpiTile label="Poor / critical" value={t?.poor_or_critical} icon={MdReportProblem} scheme="red" onClick={go(null, { type: 'filter', condition: 'poor' })} />
                        <KpiTile label="No manager" value={t?.unmanaged} icon={MdGroups} scheme="purple" onClick={go(null, { type: 'filter', unmanaged: true })} />
                        <KpiTile label="No GPS" value={t?.unlocated} icon={MdLocationOff} scheme="gray" onClick={go(null, { type: 'filter', unlocated: true })} />
                        <KpiTile label="Data complete" value={fmtPct(t?.avg_completeness)} icon={MdDataUsage} scheme="blue" hint="Average share of key fields filled in (GPS, zone, manager, install date, criticality, inspection…)" />
                    </>
                )}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={5}>
                <PuCard icon={MdLightbulb} title="Needs attention" subtitle={o ? `${o.recommendations.length} recommendation${o.recommendations.length === 1 ? '' : 's'} · updated ${new Date(o.generated_at).toLocaleTimeString()}` : 'Loading…'}
                    gridColumn={{ xl: 'span 2' }}
                    actions={<IconButton size="sm" variant="ghost" borderRadius="full" icon={<Icon as={MdRefresh} />} aria-label="Refresh intelligence" onClick={intel.refresh} />}>
                    {!o ? <Skeleton h="160px" borderRadius="lg" /> : o.recommendations.length === 0 ? (
                        <EmptyState icon={MdShield} title="Nothing urgent">Every asset is scheduled, managed and located. New findings appear here as inspections come in.</EmptyState>
                    ) : (
                        <VStack align="stretch" spacing={2}>
                            {o.recommendations.slice(0, 8).map((r) => (
                                <Flex key={r.key} p={3} borderRadius="lg" borderWidth="1px" borderColor={c.border} align="center" gap={3} _hover={{ bg: c.hoverBg }}>
                                    <Badge colorScheme={PRIORITY_SCHEME[r.priority]} borderRadius="full" px={2} flexShrink={0} textTransform="capitalize">{r.priority}</Badge>
                                    <Box flex={1} minW={0}>
                                        <Text fontSize="sm" fontWeight="700" color={c.text} noOfLines={1}>{r.title}</Text>
                                        <Text fontSize="xs" color={c.muted} noOfLines={2}>{r.detail}</Text>
                                    </Box>
                                    {r.action && (
                                        <Button size="xs" variant="outline" colorScheme="teal" borderRadius="full" rightIcon={<Icon as={MdArrowForward} />} onClick={() => onAction?.(r.action, r)} flexShrink={0}>
                                            {r.action.type === 'bulk_schedule' ? 'Schedule' : r.action.type === 'rezone' ? 'Place' : r.action.type === 'link' ? 'Open' : 'Show'}
                                        </Button>
                                    )}
                                </Flex>
                            ))}
                        </VStack>
                    )}
                </PuCard>

                <PuCard icon={MdPsychology} title="FlowSense AI" subtitle="What the system sees — and how it works">
                    {briefErr && <Text fontSize="xs" color={c.warn} mb={2}>{briefErr}</Text>}
                    {!brief ? <Skeleton h="120px" borderRadius="lg" /> : (
                        <VStack align="stretch" spacing={2}>
                            {brief.summary.map((line, i) => <Text key={i} fontSize="sm" color={c.text}>{line}</Text>)}
                            <Button size="xs" variant="ghost" alignSelf="flex-start" leftIcon={<Icon as={showGuide ? MdExpandLess : MdExpandMore} />} onClick={() => setShowGuide((v) => !v)}>
                                {showGuide ? 'Hide' : 'Explain'} how Public Utilities works
                            </Button>
                            <Collapse in={showGuide} animateOpacity>
                                <VStack align="stretch" spacing={1.5} p={3} bg={c.subtleBg} borderRadius="lg">
                                    {brief.guide.map((g, i) => <Text key={i} fontSize="xs" color={c.muted}>• {g}</Text>)}
                                </VStack>
                            </Collapse>
                            <InputGroup size="sm" mt={1}>
                                <Input placeholder="Ask FlowSense about your assets…" value={question} onChange={(e) => setQuestion(e.target.value)} borderRadius="full" bg={c.inputBg}
                                    onKeyDown={(e) => { if (e.key === 'Enter') ask(); }} maxLength={1000} aria-label="Ask FlowSense" />
                                <InputRightElement>
                                    {asking ? <Spinner size="xs" /> : <IconButton size="xs" variant="ghost" icon={<Icon as={MdSend} />} aria-label="Ask" onClick={() => ask()} isDisabled={!question.trim()} />}
                                </InputRightElement>
                            </InputGroup>
                            <HStack spacing={1} flexWrap="wrap">
                                {['Which constituency needs inspections first?', 'Explain the risk score', 'Which assets have no manager?'].map((s) => (
                                    <Button key={s} size="xs" variant="outline" borderRadius="full" fontWeight="500" onClick={() => { setQuestion(s); ask(s); }} isDisabled={asking}>{s}</Button>
                                ))}
                            </HStack>
                            {answer && (
                                <Box p={3} borderRadius="lg" bg={c.subtleBg} borderLeftWidth="3px" borderLeftColor="teal.400">
                                    <Text fontSize="sm" color={c.text} whiteSpace="pre-wrap">{answer.text}</Text>
                                    <Text fontSize="10px" color={c.muted} mt={1}>{answer.source === 'built-in' ? `FlowSense AI unavailable (${answer.error}) — answered from the built-in guide.` : `FlowSense AI${answer.model ? ` · ${answer.model}` : ''}`}</Text>
                                </Box>
                            )}
                        </VStack>
                    )}
                </PuCard>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
                <PuCard icon={MdCategory} title="Assets by type" subtitle="Overdue and poor-condition counts per type">
                    {!o ? <Skeleton h="140px" /> : o.by_type.length === 0 ? <EmptyState title="No assets yet">Import the drainage inventory from the Import tab or add assets in the Registry.</EmptyState> : (
                        <BarList rows={o.by_type.slice(0, 10).map((x) => ({ key: x.type, label: x.label, value: x.count, extra: `${x.overdue} overdue · ${x.poor} poor` }))}
                            onSelect={(r) => onAction?.({ type: 'filter', asset_type: r.key })} />
                    )}
                </PuCard>
                <PuCard icon={MdShield} title="Risk distribution" subtitle="Explainable score — open Intelligence for the factors">
                    {!o ? <Skeleton h="140px" /> : (
                        <VStack align="stretch" spacing={3}>
                            <BarList rows={['critical', 'high', 'medium', 'low'].map((k) => ({ key: k, label: k[0].toUpperCase() + k.slice(1), value: o.risk_bands[k] || 0, scheme: RISK_SCHEME[k] }))}
                                onSelect={(r) => onAction?.({ type: 'filter', risk_level: r.key })} />
                            <Tooltip label="Assets with no inspection, no failures and no model score yet — FlowSense does not guess their risk." hasArrow>
                                <Text fontSize="xs" color={c.muted}>Not enough evidence yet: {o.risk_bands.unknown ?? '—'}</Text>
                            </Tooltip>
                        </VStack>
                    )}
                </PuCard>
            </SimpleGrid>

            {children}
        </VStack>
    );
}

PuOverviewTab.propTypes = { intel: PropTypes.object.isRequired, onAction: PropTypes.func, children: PropTypes.node };
export default React.memo(PuOverviewTab);
